import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  DateRange,
  FieldDef,
  convertStringToDate,
  SelectOption,
  FilterChip,
} from './field-def';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TipologiaUtente } from 'src/app/model/utente/utente';
import { UtentiService } from 'src/app/services/utenti/utenti.service';

@Component({
  selector: 'app-search-panel',
  templateUrl: './search-panel.component.html',
  styleUrls: ['./search-panel.component.scss'],
})
export class SearchPanelComponent implements OnInit, OnChanges, OnDestroy {
  /** Identificatore interno per dedup del loader ispettori */
  private static readonly ISPETTORI_LOADER_ID = '__ispettori__';

  @Input() fields: Array<FieldDef<any>> = [];
  @Input() model: any;
  /** Click su "Filtra": rende visibili i chip e notifica il padre con `search`. */
  @Output() search = new EventEmitter<any>();
  @Output() reset = new EventEmitter<any>();

  formModel: any;

  /** Mostra i chip solo dopo un'azione esplicita (es. click su Cerca) */
  chipsVisible = false;

  /**
   * Opzioni pronte per i controlli DS, indicizzate per fieldId.
   * Nota: evitiamo di mutare `fields[]` perché è un config condiviso.
   */
  translatedOptionsByFieldId: Record<string, SelectOption[]> = {};

  /** Chip generati dinamicamente in base al config (FieldDef.chip) e al model corrente. */
  internalChips: FilterChip[] = [];

  private langSub: Subscription;

  /** Subscription per i loader di opzioni asincrone */
  private optionsLoaderSubs: Subscription[] = [];

  /** Stato interno per evitare di rilanciare continuamente lo stesso loader. */
  private optionsLoaderStateByFieldId: Record<
    string,
    { running: boolean; lastKey?: string }
  > = {};

  filtersApplied: any;

  /** Lingua corrente per il ds-date-picker (mappata dalla lingua di ngx-translate). */
  datePickerLanguage = 'it';

  /** Snapshot delle option all'apertura di una selectAll (per rollback su Annulla). */
  private selectAllOpenSnapshotByFieldId: Record<string, SelectOption[]> = {};

  /** Bozza delle selezioni mentre il menu ds-select-checkbox è aperto (applicata solo su Conferma). */
  private selectAllDraftOptionsByFieldId: Record<string, SelectOption[]> = {};

  /**
   * Copie difensive delle menuOptions per ds-select-checkbox.
   * Il DS muta l'array ricevuto con `unshift` (aggiunge "selectAll"): la copia protegge il nostro stato.
   */
  private selectCheckboxOptionsCacheByFieldId: Record<
    string,
    { source: SelectOption[]; copy: SelectOption[] }
  > = {};

  constructor(
    private readonly translate: TranslateService,
    @Optional() private readonly utentiService: UtentiService
  ) {}

  /** Costruisce le menuOptions tradotte e si sottoscrive al cambio lingua. */
  ngOnInit(): void {
    this.rebuildTranslatedOptions();

    // inizializza la lingua del date-picker e tienila aggiornata
    this.datePickerLanguage = this.mapLangToDsDatePickerLanguage(
      this.translate.currentLang || this.translate.defaultLang
    );

    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.datePickerLanguage = this.mapLangToDsDatePickerLanguage(
        this.translate.currentLang || this.translate.defaultLang
      );
      this.rebuildTranslatedOptions();
      if (this.chipsVisible) {
        this.rebuildInternalChips();
      }
    });
  }

  /** Cleanup subscriptions. */
  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }

    // chiude eventuali loader async
    this.optionsLoaderSubs.forEach(s => s.unsubscribe());
    this.optionsLoaderSubs = [];
  }

  /** Reagisce ai cambi di model/fields: clona il model, rigenera opzioni, chip e loader ispettori. */
  ngOnChanges(changes: SimpleChanges): void {
    // prima sincronizzo il formModel col nuovo model in input
    if (changes.model && this.model) {
      this.formModel = structuredClone(this.model);

      // Se il model arriva già come "filtri applicati" (es. ritorno da dettaglio con state.filtersByDetail),
      // mostro subito i chip senza richiedere un click manuale su "Filtra".
      // Heuristica minimale: se il padre passa un model con almeno un filtro valorizzato (oltre al contesto),
      // considero i filtri già applicati.
      if (this.hasAnyFilterValue(this.formModel)) {
        this.chipsVisible = true;
        // Allineo anche lo snapshot dei filtri applicati (utile per Excel/azioni)
        this.filtersApplied = structuredClone(this.formModel);
      } else {
        // Se non ci sono filtri valorizzati, non considero applicato nulla.
        this.filtersApplied = null;
      }
    }

    // resetto i filtri applicati solo se cambia isTeamView
    if (
      changes.model?.previousValue?.isTeamView !==
      changes.model?.currentValue?.isTeamView
    ) {
      this.filtersApplied = null;
    }

    if (changes.fields || changes.model) {
      this.rebuildTranslatedOptions();
      if (this.chipsVisible) {
        this.rebuildInternalChips();
      }

      // loader centralizzato per ispettori (riusabile tra pagine)
      this.loadIspettoriIfNeeded();
    }
  }

  /**
   * Ritorna true se nel model è presente almeno un filtro valorizzato.
   * Esclude i campi "di contesto" (tipologiaUtente/isTeamView/cache ispettori).
   */
  private hasAnyFilterValue(m: any): boolean {
    if (!m) {
      return false;
    }

    for (const f of this.fields || []) {
      if (!f || typeof f.get !== 'function') {
        continue;
      }

      const v = f.get(m);
      if (v == null) {
        continue;
      }

      // dateRange: range o singola data (se isRangePicker === false)
      if (f.type === 'dateRange') {
        if (f.isRangePicker === false) {
          const single = (v as any)?.value;
          if (single != null) {
            return true;
          }
        } else {
          const from = (v as any)?.from;
          const to = (v as any)?.to;
          if (from != null || to != null) {
            return true;
          }
        }
        continue;
      }

      // stringa vuota non conta
      if (typeof v === 'string') {
        if (v.trim().length > 0) {
          return true;
        }
        continue;
      }

      // boolean o oggetto/enum selezionato
      return true;
    }

    return false;
  }

  /** Copia difensiva delle opzioni per ds-select-checkbox, ricreata solo quando la sorgente cambia. */
  getSelectCheckboxOptions(fieldId: string): SelectOption[] {
    const source = this.translatedOptionsByFieldId[fieldId];
    if (!source || !source.length) {
      return [];
    }

    const cached = this.selectCheckboxOptionsCacheByFieldId[fieldId];
    if (cached && cached.source === source) {
      return cached.copy;
    }

    const copy = source.map(o => ({ ...o }));
    this.selectCheckboxOptionsCacheByFieldId[fieldId] = { source, copy };
    return copy;
  }

  /** Valuta se un campo deve essere mostrato (default: true). */
  isVisible(field: FieldDef<any>): boolean {
    return field.visibleWhen ? field.visibleWhen(this.formModel) : true;
  }

  /** True se l'ultima riga di filtri è completa (4 su 4) → i bottoni vanno su una nuova riga. */
  get lastRowFull(): boolean {
    const count = (this.fields || []).filter(f => this.isVisible(f)).length;
    return count > 0 && count % 4 === 0;
  }

  /** Valuta se un campo deve essere disabilitato (loadingWhen/disabledWhen/disabled). */
  isDisabled(field: FieldDef<any>): boolean {
    const isLoading =
      typeof field.loadingWhen === 'function'
        ? field.loadingWhen(this.model)
        : false;
    if (isLoading) {
      return true;
    }

    if (typeof field.disabledWhen === 'function') {
      return field.disabledWhen(this.formModel);
    }

    return !!field.disabled;
  }

  /** Ricostruisce le menuOptions tradotte per i controlli DS (select e selectAll). */
  private rebuildTranslatedOptions(): void {
    const next: Record<string, SelectOption[]> = {};

    for (const f of this.fields || []) {
      if (f.type !== 'select' && f.type !== 'selectAll') {
        continue;
      }

      const rawOptions =
        (typeof f.optionsFn === 'function'
          ? f.optionsFn(this.formModel)
          : f.options) || [];
      const options = rawOptions.filter(
        option => !option.visibleWhen || option.visibleWhen(this.formModel)
      );

      if (!options.length) {
        next[f.id] = [];
        continue;
      }

      if (f.type === 'select') {
        const currentValue = f.get(this.formModel);

        next[f.id] = options.map(o => {
          const label = o?.label ? this.translate.instant(o.label) : '';

          return {
            id: o.id,
            value: o.value,
            label,
            selected: this.isOptionSelected(o, currentValue),
            disabled: o.disabled,
          } as SelectOption;
        });

        continue;
      }

      // selectAll: il model contiene i value (se presenti) o le label.
      // Confronto con value (preferito) o label tradotta per ripristinare lo stato checked.
      const currentCheckedValues: any[] = Array.isArray(f.get(this.formModel))
        ? f.get(this.formModel)
        : [];
      const checkedSet = new Set(currentCheckedValues.map(v => String(v)));

      next[f.id] = options.map(o => {
        const label = o?.label ? this.translate.instant(o.label) : '';
        const matchValue =
          o.value != null
            ? checkedSet.has(String(o.value))
            : checkedSet.has(label);

        // Oggetto ISelectLabel con sole proprietà definite per compatibilità con deepEqual del DS.
        const opt: any = {
          label,
          checked: !!o.checked || matchValue,
          selected: false,
        };
        if (o.id != null) {
          opt.id = String(o.id);
        }
        if (o.value != null) {
          opt.value = String(o.value);
        }
        if (o.disabled != null) {
          opt.disabled = o.disabled;
        }

        return opt as SelectOption;
      });
    }

    this.translatedOptionsByFieldId = next;
  }

  /** Confronta una option col valore corrente (preferisce `value`, fallback su `label`). */
  private isOptionSelected(opt: any, current: any): boolean {
    if (opt == null || current == null) {
      return false;
    }

    if (Object.prototype.hasOwnProperty.call(opt, 'value')) {
      // caso oggetti clonati: se hanno un id, confronto per id
      const optVal: any = opt.value;
      if (
        optVal &&
        current &&
        typeof optVal === 'object' &&
        typeof current === 'object'
      ) {
        if (
          Object.prototype.hasOwnProperty.call(optVal, 'id') &&
          Object.prototype.hasOwnProperty.call(current, 'id')
        ) {
          return optVal.id === current.id;
        }
      }

      return opt.value === current;
    }

    return opt.label === current;
  }

  /** Replica l'effetto del pipe `titlecase` (usato per enum). */
  private toTitleCase(value: string): string {
    if (!value) {
      return value;
    }

    return value;
  }

  /** Normalizza il risultato di `textFn` in array di `{id?, text}`. */
  private normalizeChipTexts(raw: any): Array<{ id?: string; text: string }> {
    if (!raw) {
      return [];
    }

    if (typeof raw === 'string') {
      return [{ text: raw }];
    }

    if (Array.isArray(raw)) {
      return raw
        .map((x: any) => {
          if (typeof x === 'string') {
            return { text: x };
          }
          if (x && typeof x === 'object' && typeof x.text === 'string') {
            return { id: x.id, text: x.text };
          }
          return null;
        })
        .filter(Boolean) as any;
    }

    return [];
  }

  private rebuildInternalChips(): void {
    const next: FilterChip[] = [];

    for (const f of this.fields || []) {
      const chipDef = (f as any).chip;
      if (!chipDef) {
        continue;
      }

      if (
        typeof f.visibleWhen === 'function' &&
        !f.visibleWhen(this.formModel)
      ) {
        continue;
      }

      const rawText = chipDef.textFn ? chipDef.textFn(this.formModel) : null;
      const canShow =
        typeof chipDef.showWhen === 'function'
          ? chipDef.showWhen(this.formModel)
          : !!rawText;
      if (!canShow || !rawText) {
        continue;
      }

      const items = this.normalizeChipTexts(rawText);
      for (const item of items) {
        let text: any = item.text;

        const colonIdx = typeof text === 'string' ? text.indexOf(':') : -1;
        if (colonIdx > 0) {
          const left = text.substring(0, colonIdx).trim();
          const rightRaw = text.substring(colonIdx + 1).trim();

          const leftTranslated = this.translate.instant(left);

          const isEnumKey = /^[A-Z0-9_]+$/.test(rightRaw);
          const rightLooksLikeKey = rightRaw.includes('.') || isEnumKey;

          let right = rightLooksLikeKey
            ? this.translate.instant(rightRaw)
            : rightRaw;
          if (isEnumKey) {
            right = this.toTitleCase(right);
          }

          text = `${leftTranslated}: ${right}`;
        } else {
          // Chip senza ':' (tipicamente checkbox/toggle): se è una chiave i18n, la traduco interamente.
          if (typeof text === 'string' && text.includes('.')) {
            text = this.translate.instant(text);
          }
        }

        next.push({ id: item.id || chipDef.id || f.id, text });
      }
    }

    this.internalChips = next;
  }

  /** Cerca il field con chip `id` ed esegue clear. Supporta multi-chip ("fieldId__valore"). */
  private clearByChipId(chipId: string): boolean {
    // Supporto multi-chip: se l'id è del tipo "<fieldId>__<valore>",
    // rimuovo solo quel valore dall'array salvato nel model.
    if (typeof chipId === 'string' && chipId.includes('__')) {
      const [fieldId, rawValue] = chipId.split('__');
      const field = (this.fields || []).find(ff => ff.id === fieldId);
      const chipDef = field ? (field as any).chip : null;
      if (field && chipDef) {
        const current = field.get(this.formModel);
        if (Array.isArray(current)) {
          const next = current.filter(v => String(v) !== String(rawValue));
          field.set(this.formModel, next.length ? next : null);
          return true;
        }
      }
    }

    for (const f of this.fields || []) {
      const chipDef = (f as any).chip;
      if (!chipDef) {
        continue;
      }

      const id = chipDef.id || f.id;
      if (id !== chipId) {
        continue;
      }

      chipDef.clear(this.formModel);
      return true;
    }

    return false;
  }

  /** Gestisce l’input testuale DS aggiornando il model. */
  onDsInputChange(field: FieldDef<any>, event: any): void {
    const value = event?.detail?.value ?? null;
    field.set(this.formModel, value);
  }

  /** Handler DS select-list: salva il value selezionato e riallinea `selected` sulle opzioni. */
  onDsSelectChange(field: FieldDef<any>, event: any): void {
    const selectedValue = event?.detail?.option?.value ?? null;
    const selectedLabel = event?.detail?.option?.label ?? null;

    field.set(this.formModel, selectedValue);

    const currentOpts = this.translatedOptionsByFieldId[field.id] || [];
    this.translatedOptionsByFieldId = {
      ...this.translatedOptionsByFieldId,
      [field.id]: currentOpts.map(o => {
        const selectedByValue =
          selectedValue != null ? o.value === selectedValue : false;
        const selectedByLabel =
          selectedLabel != null ? o.label === selectedLabel : false;
        return {
          ...o,
          selected: selectedByValue || selectedByLabel,
        };
      }),
    };
  }

  /** Handler DS date-range: converte la stringa in `{from,to}` e salva sul model. */
  onDsDateRangeChange(field: FieldDef<any>, event: any): void {
    const raw: string = event?.detail?.dateSelection;

    if (!raw) {
      field.set(this.formModel, { from: null, to: null } as DateRange);
      return;
    }

    const parts = raw.split(' - ');
    const from = parts[0] ? convertStringToDate(parts[0]) : null;
    const to = parts[1] ? convertStringToDate(parts[1]) : null;

    field.set(this.formModel, { from, to } as DateRange);
  }

  /** Handler DS date singola: converte la stringa in `{value}` e salva sul model. */
  onDsDateChange(field: FieldDef<any>, event: any): void {
    const raw: string = event?.detail?.dateSelection;

    if (!raw) {
      field.set(this.formModel, { value: null });
      return;
    }

    const value = convertStringToDate(raw);
    field.set(this.formModel, { value });
  }

  /** Handler DS checkbox: aggiorna il model e rigenera le opzioni. */
  onDsCheckboxChange(f: FieldDef<any>, $event: any): void {
    const checked = $event?.detail?.checkbox?.checked;
    f.set(this.formModel, checked);
    this.rebuildTranslatedOptions();
  }

  /** Apertura pannello ds-select-checkbox: snapshot per rollback su "Annulla" e init bozza. */
  onDsSelectCheckboxOpen(field: FieldDef<any>): void {
    const opts = (this.translatedOptionsByFieldId[field.id] || []).map(
      (o: any) => ({ ...o })
    );
    this.selectAllOpenSnapshotByFieldId[field.id] = opts;
    this.selectAllDraftOptionsByFieldId[field.id] = opts.map(o => ({ ...o }));
  }

  /**
   * Selezione/deselezione di una option nel ds-select-checkbox.
   * Aggiorna solo il draft interno; NON toccare `translatedOptionsByFieldId` qui
   * altrimenti il DS riceve nuovi oggetti a menu aperto e crasha in `changeCheckboxValue`.
   */
  onDsSelectCheckboxOptionSelection(field: FieldDef<any>, event: any): void {
    const payloadOptions = event?.detail?.option;
    if (!Array.isArray(payloadOptions)) {
      return;
    }

    const current = (
      this.selectAllDraftOptionsByFieldId[field.id] ||
      this.translatedOptionsByFieldId[field.id] ||
      []
    ).map(o => ({ ...o }));
    if (!current.length) {
      return;
    }

    const checkedKeys = new Set<string>(
      payloadOptions.map((p: any) => (p.id ?? `label:${p.label}`) as string)
    );

    this.selectAllDraftOptionsByFieldId[field.id] = current.map(o => ({
      ...o,
      checked: checkedKeys.has((o.id ?? `label:${o.label}`) as string),
    }));
  }

  /** Click su "Annulla": ripristina lo stato checked all'apertura e azzera il model. */
  onDsSelectCheckboxLeftClick(field: FieldDef<any>): void {
    const snapshot = (this.selectAllOpenSnapshotByFieldId[field.id] || []).map(
      o => ({ ...o })
    );

    this.translatedOptionsByFieldId = {
      ...this.translatedOptionsByFieldId,
      [field.id]: snapshot,
    };

    field.set(this.formModel, null);
    delete this.selectAllDraftOptionsByFieldId[field.id];
  }

  /** Click su "Conferma": salva le opzioni checked nel model e aggiorna lo stato. */
  onDsSelectCheckboxRightClick(field: FieldDef<any>, event: any): void {
    const payloadOptions = event?.detail?.option;

    const draft = this.selectAllDraftOptionsByFieldId[field.id];
    const currentOpts = (this.translatedOptionsByFieldId[field.id] || []).map(
      o => ({ ...o })
    );

    const resolvedOptions: any[] =
      Array.isArray(payloadOptions) && payloadOptions.length
        ? payloadOptions
        : draft && draft.length
          ? draft
          : currentOpts;

    const checkedKeys = new Set<string>();
    for (const o of resolvedOptions) {
      if (o && o.checked) {
        checkedKeys.add((o.id ?? `label:${o.label}`) as string);
      }
    }

    const aligned = currentOpts.map(o => {
      const key = (o.id ?? `label:${o.label}`) as string;
      return { ...o, checked: checkedKeys.has(key) } as SelectOption;
    });

    const checkedValues = aligned
      .filter(o => !!o.checked)
      .map(o => o?.value ?? o?.label)
      .filter((v: any) => v != null && (typeof v !== 'string' || v.length > 0));

    field.set(this.formModel, checkedValues.length ? checkedValues : null);

    this.translatedOptionsByFieldId = {
      ...this.translatedOptionsByFieldId,
      [field.id]: aligned,
    };

    delete this.selectAllDraftOptionsByFieldId[field.id];
  }

  /** Click su "Filtra": rende visibili i chip, salva snapshot filtri e notifica il padre. */
  onClickSearch(): void {
    this.chipsVisible = true;
    this.rebuildInternalChips();
    this.filtersApplied = structuredClone(this.formModel);
    this.search.emit(this.filtersApplied);
  }

  /**
   * Click su "Reset" (onCustomReset).
   * Il reset visivo dei componenti DS è gestito automaticamente dal meccanismo
   * resetPair/elementResetPair della libreria DS.
   * Qui resettiamo solo lo stato applicativo Angular: model, chip, opzioni e snapshot.
   */
  onClickReset(): void {
    // 1. Azzera il formModel per ogni campo
    for (const f of this.fields) {
      if (f.type === 'dateRange') {
        f.set(
          this.formModel,
          f.isRangePicker === false
            ? { value: null }
            : ({ from: null, to: null } as DateRange)
        );
      } else if (f.type === 'checkbox') {
        f.set(this.formModel, false);
      } else {
        f.set(this.formModel, null);
      }
    }

    // 2. Pulisci chip e stato filtri applicati
    this.internalChips = [];
    this.chipsVisible = false;
    this.model = null;
    this.filtersApplied = null;

    // 3. Pulisci snapshot e draft delle select-checkbox
    this.selectAllOpenSnapshotByFieldId = {};
    this.selectAllDraftOptionsByFieldId = {};

    // 4. Rigenera le opzioni tradotte (deseleziona tutto)
    this.rebuildTranslatedOptions();

    // 5. Notifica il componente padre
    this.reset.emit(this.formModel);
  }

  /** Rimozione chip: esegue clear, rigenera opzioni/chip e ri-emette `search`. */
  onChipRemove(id: string): void {
    const didClear = this.clearByChipId(id);
    if (!didClear) {
      return;
    }

    this.rebuildTranslatedOptions();
    this.rebuildInternalChips();

    // Anche la rimozione chip è un'azione che applica i filtri: aggiorno lo snapshot per Excel.
    this.filtersApplied = { ...(this.formModel || {}) };

    // allinea subito il model usato in template (date-picker/select)
    this.model = structuredClone(this.formModel);

    this.search.emit(structuredClone(this.filtersApplied));
  }

  /** TrackBy per *ngFor su fields (riduce rerender e flicker dei componenti DS). */
  trackByFieldId(index: number, field: FieldDef<any>): string {
    return field.id;
  }

  /**
   * Loader centralizzato ispettori (attivo se isTeamView o forceIspettoriLoader).
   * Aggiorna sia formModel che model per allineare loadingWhen/disabledWhen.
   */
  private loadIspettoriIfNeeded(): void {
    const current = (this.formModel ?? this.model) as any;
    if (!current) {
      return;
    }

    const canLoad =
      current.isTeamView === true || current.forceIspettoriLoader === true;
    if (!canLoad || !this.utentiService) {
      return;
    }

    if (current.downloadUtentiIspettori == null) {
      current.downloadUtentiIspettori = false;
    }
    if (current.downloadUtentiIspettori === true) {
      return;
    }

    const loaderId = SearchPanelComponent.ISPETTORI_LOADER_ID;
    const state = this.optionsLoaderStateByFieldId[loaderId] || {
      running: false,
      lastKey: undefined,
    };
    if (state.running) {
      this.optionsLoaderStateByFieldId[loaderId] = state;
      return;
    }

    state.running = true;
    this.optionsLoaderStateByFieldId[loaderId] = state;

    const applyResult = (list: any[]) => {
      current.utentiIspettori = list;
      current.downloadUtentiIspettori = true;

      if (this.model) {
        (this.model as any).utentiIspettori = list;
        (this.model as any).downloadUtentiIspettori = true;
        this.formModel = structuredClone(this.model);
      }

      this.rebuildTranslatedOptions();
      if (this.chipsVisible) {
        this.rebuildInternalChips();
      }
    };

    const sub = this.utentiService
      .getUsers([
        TipologiaUtente.ISPETTORE_ASSQUAL,
        TipologiaUtente.RESPONSABILE_ASSQUAL,
      ])
      .pipe(
        finalize(() => {
          const st = this.optionsLoaderStateByFieldId[loaderId] || {
            running: false,
            lastKey: undefined,
          };
          st.running = false;
          this.optionsLoaderStateByFieldId[loaderId] = st;

          this.rebuildTranslatedOptions();
          if (this.chipsVisible) {
            this.rebuildInternalChips();
          }
        })
      )
      .subscribe({
        next: (res: any) => {
          const anyRes: any = res as any;
          const list =
            anyRes && anyRes.errorMex == null && anyRes.respObj
              ? anyRes.respObj
              : [];
          applyResult(list);
        },
        error: (err: any) => {
          console.error('Error catch', err);
          applyResult([]);
        },
      });

    this.optionsLoaderSubs.push(sub);
  }

  /** Mappa la lingua di ngx-translate nel formato atteso da ds-date-picker. */
  private mapLangToDsDatePickerLanguage(lang?: string | null): string {
    const normalized = (lang || 'it').toLowerCase();
    // La DS library tipicamente accetta un codice breve ('it', 'en').
    if (normalized.startsWith('en')) {
      return 'en';
    }

    return 'it';
  }
}
